import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/header/Header';
import images from '../../constants/images';
import { useTranslation } from 'react-i18next';
import Map from '../../components/map/Map';
import Footer from '../../components/footer/Footer';
import '../../assets/styles/global.css';
import styles from '../about/About.module.css';
import { useNavigate } from 'react-router-dom';
import CountUp from 'react-countup';
import { motion, useInView } from 'framer-motion';
import CursorFollower from '../../components/cursor/Cursor';
import './shiny.effect.css';
import '../spinner.css';
import emailjs from '@emailjs/browser';
import { useTheme, FormHelperText } from '@mui/material';

const Home: React.FC = () => {
  const slides = [
    {
      src: images.homeSlideOne, // replace with actual image imports
      alt: 'Slide 1',
      title: 'homeSection.slideOneTitle',
      description: 'homeSection.slideOneText',
    },
    {
      src: images.homeSlideTwo,
      alt: 'Slide 2',
      title: 'homeSection.slideTwoTitle',
      description: 'homeSection.slideTwoText',
    },
    {
      src: images.homeSlideThree,
      alt: 'Slide 3',
      title: 'homeSection.slideThreeTitle',
      description: 'homeSection.slideThreeText',
    },
  ];

  const descriptionVariants = {
    hidden: { opacity: 0, y: 50 }, // Starts below
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut', delay: 0.2 },
    }, // Delayed animation
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 50 }, // Starts below
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut', delay: 0.6 },
    }, // Appears after description
  };

  const choose = [
    {
      image: images.quality,
      activity: 'whyChooseUs.quality',
    },
    {
      image: images.designs,
      activity: 'whyChooseUs.designs',
    },
    {
      image: images.sustainability,
      activity: 'whyChooseUs.sustainability',
    },
    {
      image: images.customer,
      activity: 'whyChooseUs.customer',
    },
  ];

  const serve = [
    {
      image: images.automotive,
      service: 'industries.automotive',
    },
    {
      image: images.industrial,
      service: 'industries.industrial',
    },
    {
      image: images.architecture,
      service: 'industries.architecture',
    },
    {
      image: images.aeronautical,
      service: 'industries.aeronautical',
    },
    {
      image: images.energy,
      service: 'industries.energy',
    },
    {
      image: images.conductor,
      service: 'industries.conductor',
    },
    {
      image: images.heat,
      service: 'industries.heat',
    },
    {
      image: images.maritime,
      service: 'industries.maritime',
    },
  ];

  const { t } = useTranslation('global');
  const [isLoading, setIsLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isChanging, setIsChanging] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [triggerCountUp, setTriggerCountUp] = useState(false);
  const [responseMsg, setResponseMsg] = useState('');
  const [responseMsgColor, setResponseMsgColor] = useState('success');
  const navigate = useNavigate();
  const theme: any = useTheme();

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    const handleScroll = () => {
      setShowButton(window.scrollY > 400); // Show button after scrolling 200px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      handleNextSlide();
    }, 3000);
    return () => clearInterval(interval);
  }, [currentSlide]);

  const handleNextSlide = () => {
    setIsChanging(true);
    setTimeout(() => {
      setIsChanging(false);
      setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 300);
  };

  const handleSlideChange = (index: number) => {
    setCurrentSlide(index);
  };

  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Stops observing after animation runs once
        }
      },
      { threshold: 0.2 }, // Trigger when 20% of the section is visible
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  const resetForm = () => {
    setFormData({
      from_name: '',
      email: '',
      mobile_number: '',
      subject: '',
      message: '',
    });

    setErrors({
      email: '',
    });
  };

  const [formData, setFormData] = useState({
    from_name: '',
    email: '',
    mobile_number: '',
    subject: '',
    message: '',
  });

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });
  };

  const [errors, setErrors] = useState({
    email: '', // Only email will be validated
  });

  const validate = () => {
    const newErrors = { ...errors };
    let valid = true;

    // Email validation: Check if it's a valid email format
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailPattern.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
      valid = false;
    } else {
      newErrors.email = '';
    }

    setErrors(newErrors);
    return valid;
  };

  const sendEmail = (e: any) => {
    e.preventDefault();

    if (validate()) {
      const templateParams = {
        from_name: formData.from_name,
        email: formData.email,
        mobile_number: formData.mobile_number,
        subject: formData.subject,
        message: formData.message,
      };

      emailjs
        .send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID ?? '', // Replace with your service ID
          process.env.REACT_APP_EMAILJS_INQUIRY_TEMPLATE_ID ?? '', // Replace with your template ID
          templateParams,
          {
            publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
          }, // Replace with your user ID from Email.js
        )
        .then(
          (response) => {
            console.log('Email sent successfully:', response);
            setResponseMsgColor('success');
            setResponseMsg(
              'Thank you for your inquiry! We will get back to you shortly.',
            );
            setTimeout(() => {
              setResponseMsg('');
            }, 3000);
            resetForm(); // <-- Clear the form here
          },
          (error) => {
            console.log('Email sending error:', error);
            setResponseMsgColor('error');
            setResponseMsg(
              'Oops! Something went wrong. Please try again later.',
            );
            setTimeout(() => {
              setResponseMsg('');
            }, 2000);
          },
        );
    } else {
      console.log('Validation failed!');
    }
  };
  useEffect(() => {
    const hasLoadedBefore = sessionStorage.getItem('hasLoadedBefore');

    const timer = setTimeout(
      () => {
        setIsLoading(false);
        sessionStorage.setItem('hasLoadedBefore', 'true'); // mark as loaded
      },
      hasLoadedBefore ? 1000 : 3000,
    ); // shorter delay if already loaded

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoading) {
          setTriggerCountUp(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );

    if (ref.current) observer.observe(ref.current);

    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, [isLoading]);

  if (isLoading) {
    return (
      <>
        <div className="loader-wrapper">
          <div className="spinner">
            <div className="cube1"></div>
            <div className="cube2"></div>
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <CursorFollower />

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Header */}
        <Header />

        {/* Slide Content */}
        {slides.map((slide, index) => (
          <motion.div
            key={index} // Ensures animation resets when slide changes
            initial={{ opacity: 0 }}
            animate={{ opacity: index === currentSlide ? 1 : 0 }}
            transition={{ duration: 0.7, ease: 'easeInOut' }}
            className="absolute inset-0"
          >
            <img
              src={slide.src}
              alt={slide.alt}
              className="object-cover w-full h-full"
            />

            {/* Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-30"></div>

            {/* Animated Content */}
            {index === currentSlide && (
              <motion.div
                key={slide.title} // Ensures text animation resets
                initial="hidden"
                animate="visible"
                className="absolute inset-0 flex flex-col justify-end items-start bottom-10 md:bottom-8 lg:bottom-8 xl:bottom-8 2xl:bottom-20 px-2 xl:px-4 2xl:px-8 pb-12 text-left left-0 md:left-8"
              >
                <motion.h1
                  key={slide.title}
                  variants={titleVariants}
                  className="text-4xl md:text-5xl lg:text-6xl xl:text-6xl 2xl:text-7xl text-white font-bold pb-4 w-full md:w-3/4 xl:w-1/2 2xl:w-1/2"
                >
                  {t(slide.title)}
                </motion.h1>
                <motion.p
                  key={slide.description}
                  variants={descriptionVariants}
                  className="text-white text-base md:text-lg lg:text-xl 2xl:text-2xl w-80 lg:w-11/12 xl:w-2/3 2xl:w-2/3"
                  style={{
                    fontFamily: "'Nunito Sans', sans-serif",
                    fontWeight: 400,
                  }}
                >
                  {t(slide.description)}
                </motion.p>
              </motion.div>
            )}
          </motion.div>
        ))}
        {/* Indicator */}
        <div className="absolute bottom-14 md:bottom-14 lg:bottom-14 xl:bottom-14 2xl:bottom-24 left-3 md:left-12 2xl:left-16 flex space-x-2">
          {slides.map((_, index) => (
            <div
              key={index}
              onClick={() => handleSlideChange(index)}
              className={`w-10 h-2 md:w-10 md:h-2 transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-red-500 w-10 h-2 md:w-10 md:h-2'
                  : 'bg-white w-2 h-2 md:w-2 md:h-2'
              }`}
            ></div>
          ))}
        </div>
        {/* WhatsApp Floating Button */}
        <a
          href="https://wa.me/919909998890"
          target="_blank"
          rel="noopener noreferrer"
          className={`fixed bottom-10 right-5 sm:right-4 md:right-4 lg:right-8 xl:right-10 bg-green-500 text-white rounded-full p-2 lg:p-3 shadow-lg hover:bg-green-600 transition z-50 ${styles.whatsappBackground}`}
        >
          {/* WhatsApp Image Icon */}
          <img
            src={images.whatsapp_white}
            alt="WhatsApp Icon"
            className={`w-6 lg:w-7 h-auto ${styles.whatsappIcon}`}
          />
        </a>
      </section>

      {/* About details */}
      <section className="relative font py-12 md:py-18 2xl:py-32 px-2 md:px-6 lg:px-10 xl:px-12 2xl:px-20 bg-no-repeat bg-cover bg-bottom">
        {/* Background Image */}
        <img
          src={images.about_mobile_bg}
          alt="Background"
          className={`absolute object-cover xl:hidden lg:hidden md:hidden sm:block bottom-2/4 left-0 sm:left-12 z-0 ${styles.background}`}
          style={{ objectPosition: 'bottom' }}
        />
        <img
          src={images.about}
          alt="Background"
          className="absolute object-cover  left-0 w-full h-auto hidden lg:block -bottom-35  lg:py-4 xl:py-3 2xl:py-1 z-0"
          style={{ objectPosition: 'bottom' }}
        />

        {/* Overlay for darker effect */}
        <div className="absolute inset-0 z-10"></div>

        <div className="relative z-20 flex flex-col">
          <div className="lg:w-5/6 pb-4 md:pb-8 lg:pb-0 px-4 lg:px-0">
            <p
              className="font text-lg md:text-xl lg:text-base xl:text-lg 2xl:text-xl text-black font-normal pb-6"
              style={{
                fontFamily: "'Nunito Sans', sans-serif",
                fontWeight: 400,
              }}
            >
              {t('aboutSection.paragraph')}
            </p>
          </div>
          <div
            ref={ref}
            className="flex flex-col md:flex-row pt-0 md:pt-10 px-2 md:px-0 md:space-x-10"
          >
            <div>
              <h1
                className="text-7xl font-bold font md:pb-0 pb-5"
                style={{
                  fontFamily: "'Nunito Sans', sans-serif",
                  fontWeight: 700,
                }}
              >
                {triggerCountUp ? (
                  <CountUp start={0} end={600} duration={1.5} />
                ) : (
                  0
                )}
              </h1>
              <p
                className="text-red-500 font text-2xl font-normal"
                style={{
                  fontFamily: "'Nunito Sans', sans-serif",
                  fontWeight: 400,
                }}
              >
                {t('aboutSection.product')}
              </p>
            </div>
            <div className="w-2 h-2 bg-red-500 mt-7 mb-7 md:mt-14"></div>
            <div>
              <h1
                className="text-7xl font-bold font md:pb-0 pb-5"
                style={{
                  fontFamily: "'Nunito Sans', sans-serif",
                  fontWeight: 700,
                }}
              >
                {triggerCountUp ? (
                  <CountUp start={0} end={187} duration={2} suffix="K" />
                ) : (
                  0
                )}
              </h1>
              <p
                className="text-red-500 font text-2xl font-normal"
                style={{
                  fontFamily: "'Nunito Sans', sans-serif",
                  fontWeight: 400,
                }}
              >
                {t('aboutSection.plant')}
              </p>
            </div>
            <div className="w-2 h-2 bg-red-500 mt-7 mb-7 md:mt-14"></div>
            <div>
              <h1
                className="text-7xl font-bold font md:pb-0 pb-5"
                style={{
                  fontFamily: "'Nunito Sans', sans-serif",
                  fontWeight: 700,
                }}
              >
                {triggerCountUp ? (
                  <CountUp start={0} end={4200} duration={2} separator="" />
                ) : (
                  0
                )}
              </h1>
              <p
                className="text-red-500 font text-2xl font-normal"
                style={{
                  fontFamily: "'Nunito Sans', sans-serif",
                  fontWeight: 400,
                }}
              >
                {t('aboutSection.annual')}
              </p>
            </div>
          </div>
          <div className="flex pt-4 md:pt-20">
            <a
              href="/about-us"
              className="font text-xl 2xl:text-2xl font-normal"
            >
              {t('aboutSection.learn')}
            </a>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-6 h-6 text-black ml-2 cursor-pointer 2xl:w-8 2xl:h-8"
              onClick={() => navigate('/about-us')}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>

        {/* Scroll to Top Button */}
        {/* <div className="fixed  absolute xl:top-1/3 lg:top-2/4 md:top-1/4 top-1/3 right-0 transform">
            <div className="py-8 px-3 md:py-6 md:px-6 lg:py-7 xl:py-10 xl:px-16"></div>
            <div className="absolute flex flex-col lg:-right-4 xl:right-0 translate-y-1/2 hidden lg:flex">
              <img
                src={images.up}
                alt="UpArrow"
                className="flex justify-center 2xl:pb-10 2xl:h-20 xl:pb-8 xl:h-16 lg:h-12 lg:pb-6"
              />
              <a
                href="#top"
                className="flex text-sm md:text-lg lg:text-sm xl:text-base 2xl:text-xl text-red-500 rotate-90"
              >
                {t("whoWeAre.top")}
              </a>
            </div>
          </div> */}
        {showButton && (
          <div className="fixed xl:top-1/3 lg:top-2/4 md:top-1/4 top-1/3 right-0 transform z-50 ">
            <div className="py-8 px-3 md:py-6 md:px-6 lg:py-7 xl:py-10 xl:px-16 "></div>
            <div
              className="absolute flex flex-col lg:-right-4 xl:right-0 translate-y-1/2 hidden lg:flex "
              onClick={scrollToTop}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={images.up}
                alt="UpArrow"
                className={`flex justify-center xl:pb-8 xl:h-16 lg:h-12 lg:pb-6 ${styles.icon}`}
              />
              <button
                className={`flex text-sm md:text-lg lg:text-sm xl:text-base 2xl:text-lg text-red-500 rotate-90 transition duration-300 ${styles.top}`}
              >
                {t('whoWeAre.top')}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* OurProcess detail tablet to all screen */}
      <section className="relative hidden md:flex md:flex-row flex-col font ">
        {/* Background Image */}
        <img src={images.ourProcess} alt={'Our Process'} className={`w-full`} />
        {/* Overlay Box */}
        <div
          className="absolute left-16 top-0 bottom-0 2xl:h-4/5 h-5/6 md:w-10/12 lg:w-6/12 xl:w-1/2 bg-black md:bg-opacity-100 text-white flex flex-col justify-center p-8 md:p-8 lg:p-12 overlay-with-shadow"
          style={{ fontFamily: "'Nunito Sans', sans-serif", fontWeight: 600 }}
        >
          <h1 className="text-3xl md:text-4xl 2xl:text-6xl font-semibold mb-4">
            {t('ourProcessSection.ourProcessTitle')}
          </h1>
          <p className="text-base md:text-lg 2xl:text-xl mb-8 leading-relaxed">
            {t('ourProcessSection.para1')}
          </p>
          <p className="text-base md:text-lg 2xl:text-xl md:mb-4 xl:mb-8 leading-relaxed">
            {t('ourProcessSection.para2')}
          </p>
          <div className="flex md:pt-0 xl:pt-20">
            <a
              href="/our-process"
              className="font text-xl 2xl:text-2xl font-normal"
              style={{
                fontFamily: "'Nunito Sans', sans-serif",
                fontWeight: 400,
              }}
            >
              {t('aboutSection.learn')}
            </a>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-6 h-6 text-white ml-2 2xl:w-8 2xl:h-8 cursor-pointer"
              onClick={() => navigate('/our-process')}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </section>

      {/* OurProcess detail Mobile screen */}
      <section className="relative flex flex-col md:hidden font">
        {/* Background Image */}
        <div className="w-full">
          <img
            src={images.ourProcess}
            alt="Our Process"
            className="w-full object-cover"
          />
        </div>

        {/* Overlay Box */}
        <div className="flex items-center justify-center bg-black text-white p-4 sm:p-8 w-full relative overlay-with-shadow">
          <div className="max-w-lg">
            <h1 className="text-3xl md:text-4xl 2xl:text-6xl font-semibold mb-4">
              {t('ourProcessSection.ourProcessTitle')}
            </h1>
            <p className="text-base md:text-lg 2xl:text-xl mb-8 leading-relaxed">
              {t('ourProcessSection.para1')}
            </p>
            <p className="text-base md:text-lg 2xl:text-xl mb-8 leading-relaxed">
              {t('ourProcessSection.para2')}
            </p>
            <div className="flex items-center">
              <a
                href="/our-process"
                className="text-xl 2xl:text-2xl font-normal"
              >
                {t('aboutSection.learn')}
              </a>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="w-6 h-6 text-white ml-2 2xl:w-8 2xl:h-8 cursor-pointer"
                onClick={() => navigate('/our-process')}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us? */}
      <section
        ref={sectionRef}
        className="relative font py-10 px-6 lg:py-16 lg:px-14 2xl:py-20 2xl:px-18 back-color"
      >
        <h1 className="title font-bold text-3xl md:text-4xl 2xl:text-5xl items-center pb-6 2xl:pb-12">
          {t('whyChooseUs.whyChooseUs?')}
        </h1>
        <div
          className={`flex flex-col md:flex-row space-y-4 md:space-y-0 space-x-0 md:space-x-5 items-start justify-between`}
        >
          {choose.map((choose, index) => (
            <div
              key={index}
              className={`shiny-box flex flex-col justify-center bg-white rounded-md w-64 p-4 2xl:w-80 2xl:p-6 ${
                isVisible ? 'visible' : ''
              }`}
              style={{ boxShadow: '0px 2px 10px #1010100D' }}
            >
              <img
                src={choose.image}
                alt={t(choose.activity)}
                className={`2xl:w-16 w-12 h-auto mb-4 object-cover items-start ${styles.team}`}
              />
              <h3
                className={`title font-semibold 2xl:text-2xl xl:text-xl md:text-base text-xl ${styles.name}`}
              >
                {t(choose.activity)}
              </h3>
            </div>
          ))}
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="relative font py-10 md:py-16 px-8 sm:px-16 2xl:py-20 2xl:px-20">
        {/* Title */}
        <h1
          style={{
            fontFamily: "'Nunito Sans', sans-serif",
          }}
          className="font-bold text-4xl 2xl:text-5xl text-center pb-10 2xl:pb-14"
        >
          {t('industries.industriesWeServe')}
        </h1>

        {/* Images Layout - Ensuring 4 Per Row on Large Screens, 2 Per Row on Tablets */}
        <div className="relative flex flex-wrap justify-center gap-6 md:gap-8 lg:gap-8 xl:gap-12">
          {serve.map((item, index) => (
            <div
              key={index}
              className="flex flex-col justify-center items-center space-x-2 2xl:space-x-0 w-[45%] md:w-[30%] lg:w-[22%] min-w-[150px]" // 2 per row on tablets, 4 per row on larger screens
            >
              {/* Special Overlay Image for 4th Item */}
              {index === 3 && (
                <img
                  src={images.rectangle}
                  alt="Rectangle"
                  className="absolute hidden lg:block lg:w-[60px] lg:h-[60px] xl:w-[70px] xl:h-[70px] 2xl:w-[80px] 2xl:h-[80px] lg:-right-3 xl:-right-3 -top-2 2xl:right-[180px] z-[-1]"
                />
              )}

              {/* Service Image */}
              <img
                src={item.image}
                alt={t(item.service)}
                className="w-full object-cover mb-4"
              />

              {/* Service Title */}
              <h3
                className={`title font-medium text-lg md:text-lg lg:text-base xl:text-xl 2xl:text-2xl text-center items-center h-[48px] md:h-[52px] lg:h-[52px] xl:h-[56px] 2xl:h-[60px] leading-tight break-words overflow-hidden`}
              >
                {t(item.service)}
              </h3>
            </div>
          ))}
        </div>
      </section>

      {/* Get In Touch */}
      <section className="relative flex pl-0 h-auto back-color">
        {/* Background Image */}
        <img
          src={images.mapBackground}
          alt="Map"
          className="absolute md:flex hidden inset-0 md:h-full object-cover top-2/4  sm:1/2 md:top-0 lg:left-56 xl:left-48 2xl:left-0 2xl:w-full "
        />

        {/* Semi-transparent Overlay to soften the background image */}
        <div className="absolute inset-0 bg-white h-auto opacity-20 "></div>

        {/* Contact Form Box */}
        <div
          className="relative z-10 bg-white p-2 md:p-8 mt-8 mb-16 md:mt-16 w-10/12 lg:max-w-xl h-2/3 2xl:max-w-2xl max-w-md mx-auto lg:ml-10 lg:mx-0 xl:left-5 "
          style={{
            boxShadow: '0px 4px 12px #1010100D',
          }}
        >
          <h2 className="text-2xl 2xl:text-3xl font-bold mb-6 text-gray-800">
            {t('getInTouch.getInTouchTitle')}
          </h2>
          <form onSubmit={sendEmail} className="space-y-4 2xl:space-y-6">
            <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 2xl:space-x-6">
              <input
                type="text"
                name="from_name"
                value={formData.from_name}
                onChange={handleInputChange}
                placeholder="Your Name*"
                className="border border-black p-3 w-full md:w-1/2 focus:outline-none focus:ring-1 focus:ring-[#797979]"
                required
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Your Email*"
                className={`border border-black p-3 w-full md:w-1/2 focus:outline-none ${errors.email ? 'border-red-500' : 'focus:ring-1 focus:ring-[#797979]'}`}
                required
              />
            </div>
            <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 2xl:space-x-6">
              <input
                type="tel"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleInputChange}
                placeholder="Phone"
                className="border border-black p-3 w-full md:w-1/2 focus:outline-none focus:ring-1 focus:ring-[#797979]"
              />
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="Subject"
                className="border border-black p-3 w-full md:w-1/2 focus:outline-none focus:ring-1 focus:ring-[#797979]"
              />
            </div>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Write Here*"
              className="border border-black p-3 w-full focus:outline-none focus:ring-1 focus:ring-[#797979] h-52 md:h-44 2xl:h-60 resize"
              required
            ></textarea>
            <button
              type="submit"
              className="w-full bg-red-500 text-white py-3 font-semibold hover:bg-red-600 transition duration-300"
            >
              {t('getInTouch.send')}
            </button>
            {responseMsg && (
              <FormHelperText
                sx={{
                  color:
                    responseMsgColor === 'success'
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                  fontSize: '16px',
                  fontFamily: "'Nunito Sans', sans-serif",
                }}
              >
                {responseMsg}
              </FormHelperText>
            )}
          </form>
        </div>
        {/* Background Image */}
        <img
          src={images.mapBackground}
          alt="Map"
          className="md:hidden absolute inset-0 object-cover md:w-full w-80 right-32 top-3/4 sm:h-10"
        />
      </section>

      {/* Map Section */}

      <Map />

      {/* Footer Section */}

      <Footer />
    </>
  );
};

export default Home;
